import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Volume2, Lightbulb, RotateCcw } from "lucide-react";
import { CharacterById } from "@/components/LanguageCharacters";

function shuffleOnce(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function speak(text, lang) {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang || "en-US";
    u.rate = 0.9;
    window.speechSynthesis.speak(u);
  } catch (_) {
    // noop
  }
}

export default function LanguageLesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState(null); // { correct, expected }
  const [perAnswerResults, setPerAnswerResults] = useState([]); // for local history & submit
  const [finalResult, setFinalResult] = useState(null);
  const [hintVisible, setHintVisible] = useState(false);
  const [coachMessage, setCoachMessage] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const character = useMemo(() => localStorage.getItem("lang_guide") || "owl", []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/languages/lessons/${id}`);
        setLesson(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Lección no encontrada");
        navigate("/idiomas");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) return <div className="text-[#374151]">Cargando lección...</div>;
  if (!lesson) return null;

  const exercises = lesson.exercises || [];
  const total = exercises.length;
  const currentEx = exercises[index];
  const progress = Math.round(((index) / total) * 100);

  const setAnswer = (exId, val) => setAnswers((s) => ({ ...s, [exId]: val }));

  const askCoach = async (userAnswer, expected) => {
    setCoachLoading(true);
    try {
      const r = await api.post("/languages/coach", {
        lesson_id: lesson.id,
        exercise_id: currentEx.id,
        question_prompt: currentEx.prompt,
        user_answer: typeof userAnswer === "object" ? JSON.stringify(userAnswer) : String(userAnswer ?? ""),
        correct_answer: typeof expected === "object" ? JSON.stringify(expected) : String(expected ?? ""),
        target_lang: lesson.target_lang,
        character,
      });
      setCoachMessage(r.data.reply);
    } catch (e) {
      // fallback inside endpoint; keep silent
    } finally {
      setCoachLoading(false);
    }
  };

  const handleCheck = async () => {
    const ex = currentEx;
    const ans = answers[ex.id];
    if (
      ans === undefined ||
      ans === null ||
      (typeof ans === "string" && !ans.trim()) ||
      (Array.isArray(ans) && ans.length === 0)
    ) {
      toast.error("Responde primero");
      return;
    }
    try {
      const r = await api.post(`/languages/lessons/${id}/check`, {
        exercise_id: ex.id,
        answer: ans,
      });
      const { correct, expected } = r.data;
      setFeedback({ correct, expected });
      setPerAnswerResults((prev) => [...prev.filter((p) => p.exercise_id !== ex.id), { exercise_id: ex.id, correct, expected }]);
      if (!correct) {
        askCoach(ans, expected);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error validando respuesta");
    }
  };

  const submitAll = async () => {
    const payload = {
      answers: exercises.map((ex) => ({
        exercise_id: ex.id,
        answer: answers[ex.id] ?? (ex.type === "match_pairs" || ex.type === "order_words" ? [] : ""),
      })),
    };
    try {
      const r = await api.post(`/languages/lessons/${id}/submit`, payload);
      setFinalResult(r.data);
      if (r.data.xp_gained > 0 && !r.data.already_completed) {
        toast.success(`+${r.data.xp_gained} XP · +${r.data.coins_gained} monedas`);
      }
      if (r.data.global_points_awarded > 0) {
        toast.success(`+${r.data.global_points_awarded} puntos globales`);
      }
      (r.data.new_badges || []).forEach((b) => {
        toast.success(`¡Nueva insignia! ${b.icon} ${b.name}`);
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando resultados");
    }
  };

  const goNext = () => {
    setFeedback(null);
    setHintVisible(false);
    setCoachMessage(null);
    if (index + 1 < total) {
      setIndex(index + 1);
    } else {
      submitAll();
    }
  };

  if (finalResult) {
    return (
      <ResultsView
        result={finalResult}
        lesson={lesson}
        onRetry={() => {
          setFinalResult(null);
          setPerAnswerResults([]);
          setIndex(0);
          setAnswers({});
          setFeedback(null);
        }}
        onBack={() => navigate(`/idiomas/${lesson.target_lang}`)}
        character={character}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-28" data-testid="language-lesson">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/idiomas/${lesson.target_lang}`)}
          className="text-[#6B7280] hover:text-[#1B2E7A]"
          data-testid="lesson-back"
          aria-label="Salir"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 h-2.5 bg-[#F3F4F6] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1B2E7A] rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid="lesson-progress-bar"
          />
        </div>
        <span className="text-xs text-[#6B7280] tabular-nums">
          {index + 1}/{total}
        </span>
      </div>

      <div className="text-center">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{lesson.title}</p>
      </div>

      <ExerciseCard
        key={currentEx.id}
        exercise={currentEx}
        answer={answers[currentEx.id]}
        setAnswer={(v) => setAnswer(currentEx.id, v)}
        hintVisible={hintVisible}
      />

      {coachMessage && (
        <div className="flex items-start gap-3 bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-2xl p-4" data-testid="coach-bubble">
          <div className="shrink-0">
            <CharacterById id={character} size={64} mood="sad" />
          </div>
          <p className="text-sm text-[#854D0E] leading-snug">{coachMessage}</p>
        </div>
      )}
      {coachLoading && (
        <p className="text-xs text-[#6B7280] text-center">Pensando una pista para ti...</p>
      )}

      {feedback ? (
        <FeedbackBar
          feedback={feedback}
          onContinue={goNext}
          isLast={index + 1 >= total}
        />
      ) : (
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => setHintVisible((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A]"
            data-testid="toggle-hint"
          >
            <Lightbulb className="h-4 w-4" /> {hintVisible ? "Ocultar pista" : "Ver pista"}
          </button>
          <button
            onClick={handleCheck}
            className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] transition-colors"
            data-testid="check-answer"
          >
            Verificar
          </button>
        </div>
      )}
    </div>
  );
}

function ExerciseCard({ exercise, answer, setAnswer, hintVisible }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8 shadow-sm" data-testid={`exercise-${exercise.id}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">
        {exerciseKindLabel(exercise.type)}
      </p>

      {exercise.type === "translate_multiple" && (
        <TranslateMultiple exercise={exercise} answer={answer} setAnswer={setAnswer} />
      )}
      {exercise.type === "listen_choose" && (
        <ListenChoose exercise={exercise} answer={answer} setAnswer={setAnswer} />
      )}
      {exercise.type === "type_answer" && (
        <TypeAnswer exercise={exercise} answer={answer} setAnswer={setAnswer} />
      )}
      {exercise.type === "match_pairs" && (
        <MatchPairs exercise={exercise} answer={answer} setAnswer={setAnswer} />
      )}
      {exercise.type === "order_words" && (
        <OrderWords exercise={exercise} answer={answer} setAnswer={setAnswer} />
      )}

      {hintVisible && exercise.hint && (
        <div className="mt-4 text-sm text-[#B45309] bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-lg px-3 py-2" data-testid="hint-text">
          💡 {exercise.hint}
        </div>
      )}
    </div>
  );
}

function exerciseKindLabel(type) {
  return (
    {
      translate_multiple: "Traduce · opción múltiple",
      listen_choose: "Escucha y elige",
      type_answer: "Escribe la traducción",
      match_pairs: "Une las palabras",
      order_words: "Ordena las palabras",
    }[type] || "Ejercicio"
  );
}

function TranslateMultiple({ exercise, answer, setAnswer }) {
  const options = useMemo(() => shuffleOnce(exercise.options || []), [exercise.id]);
  return (
    <div>
      <h2 className="font-heading text-3xl text-[#1A1A1A] mb-6">{exercise.prompt}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const selected = answer === opt;
          return (
            <button
              key={opt}
              onClick={() => setAnswer(opt)}
              className={`text-left px-4 py-4 rounded-xl border-2 transition-all ${
                selected
                  ? "border-[#1B2E7A] bg-[#1B2E7A]/5 text-[#1B2E7A] font-medium"
                  : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
              }`}
              data-testid={`option-${opt}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ListenChoose({ exercise, answer, setAnswer }) {
  const options = useMemo(() => shuffleOnce(exercise.options || []), [exercise.id]);
  return (
    <div>
      <h2 className="font-heading text-2xl text-[#1A1A1A] mb-4">{exercise.prompt}</h2>
      <button
        onClick={() => speak(exercise.audio_word, exercise.audio_lang)}
        className="mb-6 inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-[#1B2E7A] text-white hover:bg-[#13205A] transition-colors"
        data-testid="play-audio"
      >
        <Volume2 className="h-6 w-6" />
        <span className="font-medium">Escuchar</span>
      </button>
      <p className="text-xs text-[#6B7280] mb-4">
        Tu navegador lee la palabra. Sube el volumen y toca el botón para repetir.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const selected = answer === opt;
          return (
            <button
              key={opt}
              onClick={() => setAnswer(opt)}
              className={`text-left px-4 py-4 rounded-xl border-2 transition-all ${
                selected
                  ? "border-[#1B2E7A] bg-[#1B2E7A]/5 text-[#1B2E7A] font-medium"
                  : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
              }`}
              data-testid={`option-${opt}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TypeAnswer({ exercise, answer, setAnswer }) {
  return (
    <div>
      <h2 className="font-heading text-3xl text-[#1A1A1A] mb-6">{exercise.prompt}</h2>
      <input
        type="text"
        value={answer || ""}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Escribe tu respuesta..."
        className="w-full px-4 py-4 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2E7A] focus:outline-none text-lg"
        data-testid="type-input"
        autoFocus
      />
      <p className="text-xs text-[#6B7280] mt-2">Aceptamos con y sin acentos.</p>
    </div>
  );
}

function MatchPairs({ exercise, answer, setAnswer }) {
  const pairs = exercise.pairs || [];
  const rights = useMemo(() => shuffleOnce(pairs.map((p) => p.right)), [exercise.id]);
  const lefts = useMemo(() => shuffleOnce(pairs.map((p) => p.left)), [exercise.id]);
  const [leftPicked, setLeftPicked] = useState(null);
  const [rightPicked, setRightPicked] = useState(null);

  // source of truth for matched stored inside "answer" array
  const matched = useMemo(() => {
    const m = {};
    (answer || []).forEach((p) => {
      if (p && p.left && p.right) m[p.left] = p.right;
    });
    return m;
  }, [answer]);

  const save = (m) => setAnswer(Object.entries(m).map(([left, right]) => ({ left, right })));

  const tryMatch = (left, right) => {
    const next = { ...matched, [left]: right };
    save(next);
    setLeftPicked(null);
    setRightPicked(null);
  };

  const onLeftClick = (l) => {
    if (matched[l]) return;
    setLeftPicked(l);
    if (rightPicked) tryMatch(l, rightPicked);
  };
  const onRightClick = (r) => {
    const takenRights = Object.values(matched);
    if (takenRights.includes(r)) return;
    setRightPicked(r);
    if (leftPicked) tryMatch(leftPicked, r);
  };

  return (
    <div>
      <h2 className="font-heading text-2xl text-[#1A1A1A] mb-6">{exercise.prompt}</h2>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          {lefts.map((l) => {
            const done = !!matched[l];
            const active = leftPicked === l;
            return (
              <button
                key={l}
                onClick={() => onLeftClick(l)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                  done
                    ? "bg-[#1B2E7A]/10 border-[#1B2E7A]/30 text-[#6B7280] line-through"
                    : active
                    ? "border-[#F4B81F] bg-[#FEF3C7]"
                    : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
                }`}
                data-testid={`pair-left-${l}`}
                disabled={done}
              >
                {l}
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          {rights.map((r) => {
            const taken = Object.values(matched).includes(r);
            const active = rightPicked === r;
            return (
              <button
                key={r}
                onClick={() => onRightClick(r)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                  taken
                    ? "bg-[#1B2E7A]/10 border-[#1B2E7A]/30 text-[#6B7280] line-through"
                    : active
                    ? "border-[#F4B81F] bg-[#FEF3C7]"
                    : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
                }`}
                data-testid={`pair-right-${r}`}
                disabled={taken}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>
      {Object.keys(matched).length > 0 && (
        <button
          onClick={() => save({})}
          className="mt-4 text-xs text-[#6B7280] hover:text-[#DC2626] inline-flex items-center gap-1"
          data-testid="reset-pairs"
        >
          <RotateCcw className="h-3 w-3" /> Reiniciar
        </button>
      )}
    </div>
  );
}

function OrderWords({ exercise, answer, setAnswer }) {
  const allTokens = useMemo(() => shuffleOnce(exercise.tokens || []), [exercise.id]);
  const built = Array.isArray(answer) ? answer : [];
  // derive bank from all tokens - built (with duplicates handled by count)
  const bank = useMemo(() => {
    const counts = {};
    allTokens.forEach((t) => {
      counts[t] = (counts[t] || 0) + 1;
    });
    built.forEach((t) => {
      if (counts[t]) counts[t] -= 1;
    });
    const arr = [];
    allTokens.forEach((t) => {
      if ((counts[t] || 0) > 0) {
        counts[t] -= 1;
        arr.push(t);
      }
    });
    return arr;
  }, [allTokens, built]);

  const pushToken = (t) => setAnswer([...built, t]);
  const popBuilt = (idx) => setAnswer(built.filter((_, i) => i !== idx));

  return (
    <div>
      <h2 className="font-heading text-2xl text-[#1A1A1A] mb-6">{exercise.prompt}</h2>

      <div className="min-h-[72px] border-2 border-dashed border-[#E5E7EB] rounded-xl px-3 py-3 mb-4 flex flex-wrap gap-2" data-testid="built-area">
        {built.length === 0 && (
          <span className="text-xs text-[#9CA3AF] self-center">
            Toca las palabras de abajo en orden...
          </span>
        )}
        {built.map((t, i) => (
          <button
            key={`b-${i}-${t}`}
            onClick={() => popBuilt(i)}
            className="px-3 py-2 rounded-lg bg-[#1B2E7A]/10 border border-[#1B2E7A]/30 text-[#1A1A1A] text-sm"
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {bank.map((t, i) => (
          <button
            key={`bk-${i}-${t}`}
            onClick={() => pushToken(t)}
            className="px-3 py-2 rounded-lg bg-white border border-[#E5E7EB] hover:border-[#1B2E7A] text-sm"
            data-testid={`token-${t}`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

function FeedbackBar({ feedback, onContinue, isLast }) {
  const correct = feedback.correct;
  const bg = correct ? "bg-[#1B2E7A]" : "bg-[#DC2626]";
  const label = correct ? "¡Correcto!" : "Respuesta";
  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 ${
        correct ? "bg-[#EFF6FF]" : "bg-[#FFF0EC]"
      } border-t ${correct ? "border-[#1B2E7A]/30" : "border-[#DC2626]/30"} p-5 md:p-6`}
      data-testid="feedback-bar"
    >
      <div className="max-w-3xl mx-auto flex items-center gap-4">
        <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white ${bg}`}>
          {correct ? <Check className="h-6 w-6" /> : <X className="h-6 w-6" />}
        </div>
        <div className="flex-1">
          <p className={`font-heading text-xl ${correct ? "text-[#1B2E7A]" : "text-[#DC2626]"}`}>
            {label}
          </p>
          {!correct && feedback.expected && (
            <p className="text-sm text-[#6B7280]">
              La respuesta correcta es:{" "}
              <span className="font-medium text-[#1A1A1A]">
                {typeof feedback.expected === "string"
                  ? feedback.expected
                  : Array.isArray(feedback.expected)
                  ? feedback.expected.join(" ")
                  : Object.entries(feedback.expected)
                      .map(([k, v]) => `${k}→${v}`)
                      .join(", ")}
              </span>
            </p>
          )}
        </div>
        <button
          onClick={onContinue}
          className={`px-6 py-3 rounded-xl text-white font-medium ${bg} hover:opacity-90 transition-opacity`}
          data-testid="feedback-continue"
        >
          {isLast ? "Terminar" : "Continuar"}
        </button>
      </div>
    </div>
  );
}

function ResultsView({ result, lesson, onRetry, onBack, character }) {
  const pct = result.accuracy;
  const pass = pct >= 60;
  return (
    <div className="max-w-2xl mx-auto" data-testid="lesson-results">
      <div className="bg-white rounded-3xl border border-[#E5E7EB] p-8 md:p-10 text-center shadow-sm">
        <div className="flex justify-center mb-4">
          <CharacterById id={character} size={160} mood={result.perfect ? "cheer" : pass ? "idle" : "sad"} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Lección completada</p>
        <h1 className="font-heading text-4xl text-[#1A1A1A] mb-2">
          {result.perfect ? "¡Perfecto!" : pass ? "¡Bien hecho!" : "Sigue practicando"}
        </h1>
        {result.coach_message && (
          <p className="text-[#6B7280] max-w-md mx-auto mb-6">{result.coach_message}</p>
        )}

        <div className="grid grid-cols-3 gap-3 max-w-md mx-auto mb-6">
          <StatTile label="Aciertos" value={`${result.correct}/${result.total}`} />
          <StatTile label="Precisión" value={`${result.accuracy}%`} />
          <StatTile label="XP" value={`+${result.xp_gained}`} accent="#F4B81F" />
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-[#6B7280] mb-6 flex-wrap">
          <Badge emoji="🪙" label={`+${result.coins_gained} monedas`} />
          <Badge
            emoji="🔥"
            label={`Racha: ${result.streak_days} día${result.streak_days !== 1 ? "s" : ""}`}
          />
          {result.global_points_awarded > 0 && (
            <Badge emoji="⭐" label={`+${result.global_points_awarded} pts globales`} />
          )}
        </div>

        {result.new_badges && result.new_badges.length > 0 && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">¡Nuevas insignias!</p>
            <div className="flex justify-center gap-3 flex-wrap">
              {result.new_badges.map((b) => (
                <div
                  key={b.code}
                  className="bg-[#FEF3C7] border border-[#F4B81F]/50 rounded-2xl px-4 py-3 text-center min-w-[110px]"
                  data-testid={`new-badge-${b.code}`}
                >
                  <p className="text-3xl leading-tight">{b.icon}</p>
                  <p className="text-xs font-medium text-[#1A1A1A] mt-1">{b.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.world_complete && (
          <div className="bg-[#EFF6FF] border border-[#1B2E7A]/30 rounded-xl p-4 mb-6">
            <p className="text-sm text-[#1B2E7A] font-medium">🗺️ ¡Completaste un mundo entero!</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] transition-colors"
            data-testid="results-back"
          >
            Volver al mapa
          </button>
          <button
            onClick={onRetry}
            className="px-6 py-3 rounded-xl bg-white border border-[#E5E7EB] text-[#6B7280] hover:text-[#1B2E7A] hover:border-[#1B2E7A] transition-colors"
            data-testid="results-retry"
          >
            Reintentar
          </button>
        </div>

        <details className="text-left mt-8" data-testid="review-details">
          <summary className="cursor-pointer text-sm text-[#6B7280] hover:text-[#1B2E7A]">
            Ver repaso ({result.details?.length || 0} ejercicios)
          </summary>
          <ul className="mt-3 space-y-2">
            {(result.details || []).map((d) => (
              <li
                key={d.exercise_id}
                className={`text-sm px-3 py-2 rounded-lg border ${
                  d.correct
                    ? "bg-[#EFF6FF] border-[#1B2E7A]/20 text-[#1B2E7A]"
                    : "bg-[#FFF0EC] border-[#DC2626]/20 text-[#6B7280]"
                }`}
              >
                <div className="flex items-start gap-2">
                  {d.correct ? (
                    <Check className="h-4 w-4 mt-0.5 text-[#1B2E7A]" />
                  ) : (
                    <X className="h-4 w-4 mt-0.5 text-[#DC2626]" />
                  )}
                  <div>
                    <p className="font-medium">{d.prompt || "Ejercicio"}</p>
                    <p className="text-xs">
                      Tu respuesta: <span className="italic">{formatAns(d.user_answer)}</span>
                    </p>
                    {!d.correct && (
                      <p className="text-xs mt-0.5">
                        Correcta: <span className="font-medium">{formatAns(d.expected)}</span>
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </details>
      </div>
    </div>
  );
}

function formatAns(a) {
  if (a === null || a === undefined || a === "") return "(sin respuesta)";
  if (Array.isArray(a)) {
    if (a.length && typeof a[0] === "object") {
      return a.map((x) => `${x.left}→${x.right}`).join(", ");
    }
    return a.join(" ");
  }
  if (typeof a === "object") {
    return Object.entries(a).map(([k, v]) => `${k}→${v}`).join(", ");
  }
  return String(a);
}

function StatTile({ label, value, accent }) {
  return (
    <div className="bg-[#FFFFFF] rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{label}</p>
      <p className="font-heading text-2xl leading-tight" style={{ color: accent || "#1A1A1A" }}>
        {value}
      </p>
    </div>
  );
}

function Badge({ emoji, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F6] border border-[#E5E7EB] text-xs">
      <span>{emoji}</span>
      <span className="text-[#1A1A1A]">{label}</span>
    </span>
  );
}
